import React from "react";
import { FileText, AlertTriangle, Phone, Mail } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const Terms = () => {
  return (
    <div className="min-h-screen bg-white py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms of Service</h1>
          <p className="text-lg text-gray-600">
            Terms and conditions for Quality Transport Services
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Last Updated: {new Date().toLocaleDateString()}
          </p>
        </div>

        {/* Important Notice */}
        <Card className="mb-8 border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
              <span>Important Service Notice</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-amber-800">
              Quality Transport Services provides Non-Emergency Medical Transportation only. 
              For emergency medical situations, please call 911 immediately. Do not use our 
              services for urgent or life-threatening medical conditions.
            </p>
          </CardContent>
        </Card>

        {/* Terms Sections */}
        <div className="space-y-8">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-6 w-6 text-blue-600 mr-2" />
              Service Agreement
            </h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">
                By booking transportation services with Quality Transport Services ("Company," "we," "us"), 
                you ("Client," "you") agree to be bound by these Terms of Service. Please read these terms 
                carefully before using our services.
              </p>
              
              <h3 className="font-semibold text-gray-900 mb-3">Service Description:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Non-Emergency Medical Transportation (NEMT) services</li>
                <li>• Wheelchair accessible and ambulatory transportation</li>
                <li>• Local and long-distance medical transport</li>
                <li>• Door-to-door transportation services</li>
                <li>• Appointment scheduling and reminder services</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Booking and Scheduling</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">Advance Notice:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• We prefer 24 hours advance notice for long-distance trips</li>
                <li>• Same-day bookings may be accommodated based on availability</li>
                <li>• Recurring appointments can be scheduled in advance</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Confirmation Process:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• All bookings require confirmation within 24 hours</li>
                <li>• You will receive confirmation via phone call or email</li>
                <li>• Trip details may be adjusted during confirmation process</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Cancellation Policy:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Cancellations must be made at least 2 hours in advance</li>
                <li>• Late cancellations may result in fees</li>
                <li>• No-shows may be charged the full transportation fee</li>
                <li>• Emergency cancellations will be handled on a case-by-case basis</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Client Responsibilities</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">Information Accuracy:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Provide accurate pickup and destination addresses</li>
                <li>• Inform us of any mobility assistance needs</li>
                <li>• Report changes to insurance coverage promptly</li>
                <li>• Update emergency contact information as needed</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">During Transportation:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Be ready at scheduled pickup time</li>
                <li>• Follow driver safety instructions</li>
                <li>• Secure all personal belongings</li>
                <li>• Notify driver of any medical equipment needs</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Behavior Standards:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Treat drivers and staff with respect</li>
                <li>• No smoking, drinking alcohol, or illegal substances</li>
                <li>• Maintain appropriate behavior during transport</li>
                <li>• Follow all vehicle safety rules</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Payment and Insurance</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">Insurance Coverage:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• We accept most major insurance plans for covered services</li>
                <li>• Insurance verification is required before first trip</li>
                <li>• Prior authorization may be needed for certain trips</li>
                <li>• You are responsible for any non-covered portions</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Self-Pay Services:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Private pay rates available upon request</li>
                <li>• Payment due at time of service unless other arrangements made</li>
                <li>• Multiple payment methods accepted</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Billing:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Insurance claims submitted within required timeframes</li>
                <li>• You will receive explanation of benefits statements</li>
                <li>• Billing inquiries should be directed to our office</li>
                <li>• Payment plans available for qualifying circumstances</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Safety and Liability</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">Safety Measures:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• All drivers are professionally trained and background checked</li>
                <li>• Vehicles undergo regular safety inspections</li>
                <li>• Wheelchair securement systems meet federal standards</li>
                <li>• Emergency procedures are in place for all situations</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Medical Limitations:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Drivers are not medical professionals</li>
                <li>• We cannot provide medical care during transport</li>
                <li>• Clients requiring medical monitoring should arrange nursing care</li>
                <li>• Emergency medical situations require calling 911</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Liability Coverage:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• We maintain comprehensive commercial insurance</li>
                <li>• Coverage includes vehicle liability and passenger protection</li>
                <li>• Claims should be reported immediately</li>
                <li>• Detailed incident reports maintained for all occurrences</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Privacy and Confidentiality</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700 mb-4">
                We are committed to protecting your health information in accordance with HIPAA regulations. 
                Please see our Privacy Policy for detailed information about how we collect, use, and protect 
                your personal and health information.
              </p>
              
              <h3 className="font-semibold text-gray-900 mb-3">Confidentiality Agreement:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• All staff sign confidentiality agreements</li>
                <li>• Health information is only shared as necessary for service delivery</li>
                <li>• Client records are securely stored and protected</li>
                <li>• Information sharing requires client authorization except as permitted by law</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Service Modifications and Limitations</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">Weather and Road Conditions:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Services may be delayed or cancelled due to severe weather</li>
                <li>• Road closures or hazardous conditions may require route changes</li>
                <li>• Safety is our top priority in all weather decisions</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Vehicle Capacity:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Maximum capacity limits based on vehicle type</li>
                <li>• Wheelchair accessible vehicles have specific accommodation limits</li>
                <li>• Large medical equipment may require special arrangements</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Service Area:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Primary service area includes Calhoun County and surrounding areas</li>
                <li>• Long-distance trips require advance scheduling</li>
                <li>• Out-of-state transportation available with sufficient notice</li>
                <li>• Some remote locations may require additional fees</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Dispute Resolution</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-3">Complaint Process:</h3>
              <ul className="space-y-2 text-gray-700 mb-4">
                <li>• Contact our office immediately with any service concerns</li>
                <li>• We investigate all complaints promptly and thoroughly</li>
                <li>• Written complaints receive written responses within 5 business days</li>
                <li>• We work to resolve issues to your satisfaction</li>
              </ul>

              <h3 className="font-semibold text-gray-900 mb-3">Regulatory Oversight:</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• We are subject to state transportation regulations</li>
                <li>• Complaints may also be filed with appropriate regulatory agencies</li>
                <li>• We comply with all applicable federal and state requirements</li>
              </ul>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Changes to Terms</h2>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-700">
                We may modify these Terms of Service at any time. Changes will be posted on our website 
                with the effective date. Continued use of our services after changes are posted constitutes 
                acceptance of the modified terms. We will notify clients of significant changes by mail or email.
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Contact Information</h2>
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <p className="text-gray-700 mb-4">
                For questions about these Terms of Service or our services:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Quality Transport Services</h4>
                  <div className="space-y-2 text-gray-700">
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4" />
                      <span>(269) 248-3514</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4" />
                      <span>qualitytransportservices42@gmail.com</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Business Hours:</h4>
                  <div className="space-y-1 text-gray-700 text-sm">
                    <p>Monday-Thursday: 8:00 AM - 5:00 PM</p>
                    <p>Friday: 8:00 AM - 4:00 PM</p>
                    <p>Saturday-Sunday: 8:00 AM - 12:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Effective Date */}
        <div className="mt-12 p-6 bg-gray-100 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-2">Effective Date</h3>
          <p className="text-gray-700">
            These Terms of Service are effective as of {new Date().toLocaleDateString()} and apply to all 
            services provided by Quality Transport Services.
          </p>
        </div>

        {/* Contact CTA */}
        <div className="mt-8 bg-blue-900 text-white rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold mb-4">Questions About Our Terms?</h3>
          <p className="text-blue-100 mb-6">
            Our team is available to clarify any questions about our terms of service.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:(269) 248-3514" className="bg-amber-500 hover:bg-amber-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors">
              Call (269) 248-3514
            </a>
            <a href="mailto:qualitytransportservices42@gmail.com" className="border border-white text-white hover:bg-white hover:text-blue-900 px-6 py-3 rounded-lg font-semibold transition-colors">
              Email Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;